import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useSyncWithFirebaseMutation } from "store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { useConfirmDialog } from "contexts/confirmDialog";
import { useNotifyStatus } from "contexts/status";
import { TabSpec, TitledTabs } from "components/common/titledTabs";
import { useUserAdminState } from "components/users/userAdminState";
import { UserTab } from "components/users/userTab";
import {
  PendingInvitesDataGrid,
  useInviteCsvColumns,
} from "components/users/pendingInvitesDataGrid";
import { UserFilterBar } from "components/users/userFilterBar";
import { UserBulkActionBar } from "components/users/userBulkActionBar";
import { useUserCsvColumns } from "components/users/userGridColumns";
import { UserDrawer } from "components/users/userDrawer";
import { AddUsersDialog } from "components/users/addUsersDialog";
import { SignInLinkDialog } from "components/users/signInLinkDialog";

type UserAdminTab = "users" | "invites" | "serviceAccounts";

export const ManageUsers = () => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const confirm = useConfirmDialog();
  const notify = useNotifyStatus();
  const [syncWithFirebase] = useSyncWithFirebaseMutation();

  const [tab, setTab] = useState<UserAdminTab>("users");
  const [addUsersOpen, setAddUsersOpen] = useState(false);

  const state = useUserAdminState({ tenant });
  const userCsvColumns = useUserCsvColumns();
  const inviteCsvColumns = useInviteCsvColumns();

  const handleSync = async () => {
    const confirmed = await confirm({
      title: t("syncWithFirebase"),
      message: t("syncWithFirebaseConfirmation"),
    });
    if (!confirmed) return;
    await syncWithFirebase({ tenantName: tenant })
      .unwrap()
      .catch(() => {
        notify({ type: "error", text: t("couldntSyncFirebase") });
      });
  };

  const handleDeleteSelectedInvites = async () => {
    const confirmed = await confirm({
      title: t("deleteInvites"),
      message: t("confirmDeleteInvites", {
        count: state.invitesSelection.length,
      }),
    });
    if (!confirmed) return;
    await state.bulkDeleteInvites(state.invitesSelection);
    state.setInvitesSelection([]);
  };

  if (!tenant) {
    return (
      <Container>
        <Alert severity="warning">{t("pleaseSelectATenant")}</Alert>
      </Container>
    );
  }

  const invitesContent = (
    <Stack spacing={2} sx={{ height: "100%", pt: 2 }}>
      <UserFilterBar
        search={state.searchText}
        onSearchChange={state.setSearchText}
        searchPlaceholder={t("searchEmail")}
        exportRows={state.filteredInvites}
        exportColumns={inviteCsvColumns}
      />
      <UserBulkActionBar
        selectedCount={state.invitesSelection.length}
        busy={state.isBulkBusy}
        onClear={() => {
          state.setInvitesSelection([]);
        }}
        onResend={() => void state.bulkResendInvites(state.invitesSelection)}
        onDelete={() => void handleDeleteSelectedInvites()}
      />
      <PendingInvitesDataGrid
        rows={state.filteredInvites}
        loading={state.isInvitesFetching}
        selection={state.invitesSelection}
        onSelectionChange={state.setInvitesSelection}
        onRowClick={state.openInviteDrawer}
        onCreateInviteLink={state.createInviteLink}
        onSendInviteEmail={(email) => void state.sendInvite(email)}
        onDeleteInvite={(email) => void state.removeInvite(email)}
      />
    </Stack>
  );

  const tabSpecs: TabSpec<UserAdminTab>[] = [
    {
      title: `${t("users")} (${state.counts.users})`,
      key: "users",
      content: (
        <UserTab state={state} variant="users" csvColumns={userCsvColumns} />
      ),
    },
    {
      title: `${t("pendingInvites")} (${state.counts.invites})`,
      key: "invites",
      content: invitesContent,
    },
    {
      title: `${t("serviceAccounts")} (${state.counts.serviceAccounts})`,
      key: "serviceAccounts",
      content: (
        <UserTab
          state={state}
          variant="serviceAccounts"
          csvColumns={userCsvColumns}
        />
      ),
    },
  ];

  return (
    <Stack sx={{ height: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1 }}
      >
        <Typography variant="h4" component="h1">
          {t("manageUsers")}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => void handleSync()}>
            {t("syncWithFirebase")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setAddUsersOpen(true);
            }}
          >
            {t("addUsers")}
          </Button>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, px: 2 }}>
        <TitledTabs
          tabSpecs={tabSpecs}
          selectedTabKey={tab}
          onChange={setTab}
        />
      </Box>
      <AddUsersDialog
        tenant={tenant}
        isOpen={addUsersOpen}
        onClose={() => {
          setAddUsersOpen(false);
        }}
      />
      <UserDrawer
        key={state.drawerKey}
        tenant={tenant}
        target={state.drawerTarget}
        onClose={state.closeDrawer}
        onSubmit={state.submitUserUpdate}
        saving={state.isSavingUser}
      />
      {state.signInLink ? (
        <SignInLinkDialog
          isOpen
          signInLink={state.signInLink}
          onClose={state.clearSignInLink}
        />
      ) : null}
    </Stack>
  );
};
